import { useState, useEffect, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { contentPlannerAPI, teamAPI } from '@/lib/api';
import {
  Plus, MoreHorizontal, Calendar as CalendarIcon, Tag, User, ListChecks,
  Trash2, X, Pencil, CheckCircle2, Circle, AlignLeft, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger
} from '@/components/ui/popover';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const LABEL_COLORS = [
  { name: 'Grijs', value: '#94A3B8' },
  { name: 'Rood', value: '#EF4444' },
  { name: 'Oranje', value: '#F97316' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Groen', value: '#10B981' },
  { name: 'Cyaan', value: '#06B6D4' },
  { name: 'Blauw', value: '#0EA5E9' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Paars', value: '#8B5CF6' },
  { name: 'Roze', value: '#EC4899' },
];

const LIST_COLORS = LABEL_COLORS;

function formatDateDisplay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short' });
}

function isOverdue(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

function toDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toTimeInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function combineToISO(dateStr, timeStr) {
  if (!dateStr) return null;
  const t = timeStr || '09:00';
  const d = new Date(`${dateStr}T${t}`);
  if (isNaN(d)) return null;
  return d.toISOString();
}

// ──────────────────────────────────────────────────────────────────────
//   CARD MODAL — full edit van een kaart
// ──────────────────────────────────────────────────────────────────────
function CardModal({ open, card, lists, users, onClose, onUpdate, onDelete }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [dueDateVal, setDueDateVal] = useState('');
  const [dueTimeVal, setDueTimeVal] = useState('');
  const [assignee, setAssignee] = useState('');
  const [listId, setListId] = useState('');
  const [labels, setLabels] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [newChecklistText, setNewChecklistText] = useState('');
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(LABEL_COLORS[0].value);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!card) return;
    setTitle(card.title || '');
    setDescription(card.description || '');
    setEditingDesc(false);
    setDueDateVal(toDateInput(card.due_date));
    setDueTimeVal(toTimeInput(card.due_date));
    setAssignee(card.assignee_user_id || '');
    setListId(card.list_id || '');
    setLabels(card.labels || []);
    setChecklist(card.checklist || []);
  }, [card]);

  const checklistProgress = useMemo(() => {
    if (!checklist.length) return 0;
    const done = checklist.filter(c => c.done).length;
    return Math.round((done / checklist.length) * 100);
  }, [checklist]);

  if (!card) return null;

  const saveField = async (patch) => {
    setSaving(true);
    try {
      const updated = await contentPlannerAPI.updateCard(card.id, patch);
      onUpdate(updated.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleTitleBlur = () => {
    const v = title.trim();
    if (v && v !== card.title) saveField({ title: v });
  };

  const handleDescriptionSave = () => {
    saveField({ description });
    setEditingDesc(false);
  };

  const handleListChange = (newListId) => {
    setListId(newListId);
    // Move card to end of new list
    contentPlannerAPI.moveCard(card.id, newListId, 9999)
      .then(res => onUpdate(res.data))
      .catch(() => toast.error('Verplaatsen mislukt'));
  };

  const handleAssigneeChange = (val) => {
    const newVal = val === '__none__' ? null : val;
    setAssignee(newVal || '');
    saveField({ assignee_user_id: newVal });
  };

  const handleDueDateChange = () => {
    if (!dueDateVal) {
      saveField({ clear_due_date: true });
    } else {
      saveField({ due_date: combineToISO(dueDateVal, dueTimeVal) });
    }
  };

  const clearDueDate = () => {
    setDueDateVal('');
    setDueTimeVal('');
    saveField({ clear_due_date: true });
  };

  const addLabel = () => {
    const name = newLabelName.trim();
    if (!name) return;
    const newLabels = [...labels, { name, color: newLabelColor }];
    setLabels(newLabels);
    setNewLabelName('');
    saveField({ labels: newLabels });
  };

  const removeLabel = (idx) => {
    const newLabels = labels.filter((_, i) => i !== idx);
    setLabels(newLabels);
    saveField({ labels: newLabels });
  };

  const addChecklistItem = async () => {
    const text = newChecklistText.trim();
    if (!text) return;
    try {
      const res = await contentPlannerAPI.addChecklistItem(card.id, text);
      setChecklist(prev => [...prev, res.data]);
      setNewChecklistText('');
      // re-fetch card via update event
      const fresh = await contentPlannerAPI.getCard(card.id);
      onUpdate(fresh.data);
    } catch {
      toast.error('Toevoegen mislukt');
    }
  };

  const toggleChecklist = async (item) => {
    try {
      await contentPlannerAPI.updateChecklistItem(card.id, item.id, { done: !item.done });
      const newList = checklist.map(c => c.id === item.id ? { ...c, done: !c.done } : c);
      setChecklist(newList);
      const fresh = await contentPlannerAPI.getCard(card.id);
      onUpdate(fresh.data);
    } catch {
      toast.error('Bijwerken mislukt');
    }
  };

  const removeChecklist = async (itemId) => {
    try {
      await contentPlannerAPI.deleteChecklistItem(card.id, itemId);
      setChecklist(prev => prev.filter(c => c.id !== itemId));
      const fresh = await contentPlannerAPI.getCard(card.id);
      onUpdate(fresh.data);
    } catch {
      toast.error('Verwijderen mislukt');
    }
  };

  const handleDelete = async () => {
    try {
      await contentPlannerAPI.deleteCard(card.id);
      onDelete(card.id);
      setConfirmDelete(false);
      onClose();
      toast.success('Kaart verwijderd');
    } catch {
      toast.error('Verwijderen mislukt');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="space-y-2">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              className="text-lg font-semibold border-0 px-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
              placeholder="Kaarttitel..."
            />
            <DialogDescription className="text-xs">
              In lijst:
              <Select value={listId} onValueChange={handleListChange}>
                <SelectTrigger className="inline-flex w-auto h-7 ml-2 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {lists.map(l => (
                    <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* Labels */}
            <div>
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <Tag className="w-4 h-4" /> Labels
              </div>
              <div className="flex flex-wrap gap-1.5 items-center">
                {labels.map((lbl, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-white"
                    style={{ background: lbl.color }}
                  >
                    {lbl.name}
                    <button onClick={() => removeLabel(idx)} className="opacity-70 hover:opacity-100">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 text-xs">
                      <Plus className="w-3 h-3 mr-1" /> Label
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64" align="start">
                    <div className="space-y-2">
                      <Input
                        placeholder="Label naam..."
                        value={newLabelName}
                        onChange={(e) => setNewLabelName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addLabel(); }}
                      />
                      <div className="grid grid-cols-5 gap-1.5">
                        {LABEL_COLORS.map(c => (
                          <button
                            key={c.value}
                            type="button"
                            onClick={() => setNewLabelColor(c.value)}
                            className={`h-7 rounded-md transition-all ${newLabelColor === c.value ? 'ring-2 ring-offset-1 ring-foreground' : ''}`}
                            style={{ background: c.value }}
                            title={c.name}
                          />
                        ))}
                      </div>
                      <Button size="sm" className="w-full" onClick={addLabel}>Toevoegen</Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Due date & Assignee row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium mb-2">
                  <CalendarIcon className="w-4 h-4" /> Vervaldatum
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={dueDateVal}
                    onChange={(e) => setDueDateVal(e.target.value)}
                    onBlur={handleDueDateChange}
                    className="text-sm"
                  />
                  <Input
                    type="time"
                    value={dueTimeVal}
                    onChange={(e) => setDueTimeVal(e.target.value)}
                    onBlur={handleDueDateChange}
                    className="text-sm w-28"
                    disabled={!dueDateVal}
                  />
                  {dueDateVal && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={clearDueDate}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {dueDateVal && (
                  <p className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Wordt automatisch gesynced met de agenda
                  </p>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-medium mb-2">
                  <User className="w-4 h-4" /> Toegewezen aan
                </div>
                <Select value={assignee || '__none__'} onValueChange={handleAssigneeChange}>
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="Niemand" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Niemand</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between text-sm font-medium mb-2">
                <span className="flex items-center gap-2"><AlignLeft className="w-4 h-4" /> Beschrijving</span>
                {!editingDesc && (
                  <Button variant="ghost" size="sm" onClick={() => setEditingDesc(true)}>
                    <Pencil className="w-3 h-3 mr-1" /> Bewerken
                  </Button>
                )}
              </div>
              {editingDesc ? (
                <div className="space-y-2">
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={5}
                    placeholder="Voeg een meer gedetailleerde beschrijving toe..."
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleDescriptionSave}>Opslaan</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setDescription(card.description || ''); setEditingDesc(false); }}>
                      Annuleren
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setEditingDesc(true)}
                  className="text-sm bg-muted/40 rounded-md p-3 min-h-[60px] cursor-pointer hover:bg-muted/60 whitespace-pre-wrap"
                >
                  {description || <span className="text-muted-foreground italic">Klik om beschrijving toe te voegen...</span>}
                </div>
              )}
            </div>

            {/* Checklist */}
            <div>
              <div className="flex items-center justify-between text-sm font-medium mb-2">
                <span className="flex items-center gap-2"><ListChecks className="w-4 h-4" /> Checklist</span>
                {checklist.length > 0 && (
                  <span className="text-xs text-muted-foreground">{checklist.filter(c => c.done).length} / {checklist.length}</span>
                )}
              </div>
              {checklist.length > 0 && (
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mb-3">
                  <div
                    className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${checklistProgress}%` }}
                  />
                </div>
              )}
              <div className="space-y-1.5">
                {checklist.map(item => (
                  <div key={item.id} className="flex items-center gap-2 group">
                    <button onClick={() => toggleChecklist(item)} className="text-muted-foreground hover:text-foreground">
                      {item.done
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        : <Circle className="w-4 h-4" />}
                    </button>
                    <span className={`flex-1 text-sm ${item.done ? 'line-through text-muted-foreground' : ''}`}>
                      {item.text}
                    </span>
                    <button
                      onClick={() => removeChecklist(item.id)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Nieuw item..."
                    value={newChecklistText}
                    onChange={(e) => setNewChecklistText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addChecklistItem(); }}
                    className="text-sm h-9"
                  />
                  <Button size="sm" onClick={addChecklistItem} disabled={!newChecklistText.trim()}>
                    Voeg toe
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4 sm:justify-between">
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="w-4 h-4 mr-1" /> Verwijderen
            </Button>
            <div className="text-xs text-muted-foreground">
              {saving ? 'Opslaan...' : 'Alle wijzigingen worden automatisch opgeslagen'}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kaart verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze actie verwijdert ook het gekoppelde agenda-event. Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
//   CARD COMPONENT (in board)
// ──────────────────────────────────────────────────────────────────────
function BoardCard({ card, onClick }) {
  const overdue = isOverdue(card.due_date);
  const checklist = card.checklist || [];
  const checklistDone = checklist.filter(c => c.done).length;
  const hasMeta = card.due_date || card.assignee_user_id || checklist.length > 0 || (card.description && card.description.length > 0);

  return (
    <div
      onClick={onClick}
      className="bg-card border border-border rounded-md p-2.5 shadow-sm hover:shadow-md hover:border-primary/40 cursor-pointer transition-all group"
    >
      {/* Labels strip */}
      {card.labels && card.labels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {card.labels.map((lbl, idx) => (
            <span
              key={idx}
              className="inline-block h-1.5 rounded-full min-w-[28px]"
              style={{ background: lbl.color }}
              title={lbl.name}
            />
          ))}
        </div>
      )}

      <p className="text-sm font-medium text-foreground leading-snug">{card.title}</p>

      {hasMeta && (
        <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-muted-foreground">
          {card.description && (
            <span title="Heeft beschrijving"><AlignLeft className="w-3 h-3" /></span>
          )}
          {checklist.length > 0 && (
            <span className={`flex items-center gap-0.5 ${checklistDone === checklist.length ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
              <ListChecks className="w-3 h-3" />
              {checklistDone}/{checklist.length}
            </span>
          )}
          {card.due_date && (
            <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${overdue ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-muted'}`}>
              <CalendarIcon className="w-3 h-3" />
              {formatDateDisplay(card.due_date)}
            </span>
          )}
          {card.assignee_name && (
            <span className="ml-auto inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/20 text-primary text-[10px] font-semibold" title={card.assignee_name}>
              {card.assignee_name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
//   LIST COMPONENT
// ──────────────────────────────────────────────────────────────────────
function BoardList({ list, cards, index, onAddCard, onCardClick, onRenameList, onDeleteList, onChangeListColor }) {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(list.title);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const submit = async () => {
    const t = newTitle.trim();
    if (!t) { setAdding(false); return; }
    await onAddCard(list.id, t);
    setNewTitle('');
    setAdding(false);
  };

  const saveTitle = async () => {
    const t = editTitle.trim();
    if (t && t !== list.title) await onRenameList(list.id, t);
    setEditing(false);
  };

  return (
    <Draggable draggableId={list.id} index={index}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="flex-shrink-0 w-[290px] bg-muted/50 rounded-lg flex flex-col max-h-full"
        >
          {/* Header */}
          <div className="flex items-center gap-1.5 p-2.5 pb-1.5" {...provided.dragHandleProps}>
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: list.color || '#94A3B8' }} />
            {editing ? (
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') { setEditTitle(list.title); setEditing(false); } }}
                autoFocus
                className="h-7 text-sm font-semibold px-1"
              />
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="flex-1 text-left text-sm font-semibold text-foreground truncate hover:bg-background/50 rounded px-1 py-0.5"
              >
                {list.title}
              </button>
            )}
            <span className="text-xs text-muted-foreground tabular-nums">{cards.length}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1 rounded hover:bg-background/70 text-muted-foreground">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setEditing(true)}>
                  <Pencil className="w-4 h-4 mr-2" /> Hernoemen
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setAdding(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Kaart toevoegen
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <p className="text-xs text-muted-foreground mb-1.5">Kleur</p>
                  <div className="grid grid-cols-5 gap-1">
                    {LIST_COLORS.map(c => (
                      <button
                        key={c.value}
                        onClick={() => onChangeListColor(list.id, c.value)}
                        className={`h-5 rounded ${list.color === c.value ? 'ring-2 ring-offset-1 ring-foreground' : ''}`}
                        style={{ background: c.value }}
                        title={c.name}
                      />
                    ))}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setConfirmDelete(true)} className="text-destructive focus:text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" /> Lijst verwijderen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Cards */}
          <Droppable droppableId={list.id} type="CARD">
            {(dropProvided, snapshot) => (
              <div
                ref={dropProvided.innerRef}
                {...dropProvided.droppableProps}
                className={`flex-1 px-2 pb-2 space-y-1.5 overflow-y-auto min-h-[20px] rounded transition-colors ${snapshot.isDraggingOver ? 'bg-primary/5' : ''}`}
              >
                {cards.map((card, cIdx) => (
                  <Draggable key={card.id} draggableId={card.id} index={cIdx}>
                    {(cardProvided, cardSnap) => (
                      <div
                        ref={cardProvided.innerRef}
                        {...cardProvided.draggableProps}
                        {...cardProvided.dragHandleProps}
                        className={cardSnap.isDragging ? 'rotate-1 opacity-90' : ''}
                      >
                        <BoardCard card={card} onClick={() => onCardClick(card)} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {dropProvided.placeholder}
              </div>
            )}
          </Droppable>

          {/* Add card */}
          <div className="p-2 pt-1">
            {adding ? (
              <div className="space-y-2">
                <Textarea
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } if (e.key === 'Escape') { setAdding(false); setNewTitle(''); } }}
                  placeholder="Voer een titel in voor deze kaart..."
                  autoFocus
                  rows={2}
                  className="text-sm resize-none"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={submit}>Kaart toevoegen</Button>
                  <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setNewTitle(''); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setAdding(true)}
                className="w-full flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-background/60 rounded px-2 py-1.5 transition-colors"
              >
                <Plus className="w-4 h-4" /> Kaart toevoegen
              </button>
            )}
          </div>

          <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Lijst verwijderen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Lijst "{list.title}" en alle {cards.length} kaarten (incl. gekoppelde agenda-events) worden verwijderd.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <AlertDialogAction onClick={() => { onDeleteList(list.id); setConfirmDelete(false); }} className="bg-destructive hover:bg-destructive/90">
                  Verwijderen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </Draggable>
  );
}

// ──────────────────────────────────────────────────────────────────────
//   PAGE
// ──────────────────────────────────────────────────────────────────────
export default function ContentPlannerPage() {
  const [lists, setLists] = useState([]);
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCard, setOpenCard] = useState(null);
  const [addingList, setAddingList] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');

  const fetchBoard = useCallback(async () => {
    try {
      const res = await contentPlannerAPI.getBoard();
      setLists(res.data.lists || []);
      setCards(res.data.cards || []);
    } catch (err) {
      toast.error('Kon board niet laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await teamAPI.members();
        console.log('Fetched team members for content planner:', res.data);
        setUsers(res.data || []);
      } catch (err) {
        console.error('Failed to fetch team members for content planner:', err);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const cardsByList = useMemo(() => {
    const map = {};
    for (const l of lists) map[l.id] = [];
    for (const c of cards) {
      if (assigneeFilter && c.assignee_user_id !== assigneeFilter) continue;
      if (!map[c.list_id]) map[c.list_id] = [];
      map[c.list_id].push(c);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => (a.position || 0) - (b.position || 0));
    return map;
  }, [lists, cards, assigneeFilter]);

  const handleAddList = async () => {
    const t = newListTitle.trim();
    if (!t) { setAddingList(false); return; }
    try {
      const res = await contentPlannerAPI.createList({ title: t });
      setLists(prev => [...prev, res.data]);
      setNewListTitle('');
      setAddingList(false);
    } catch {
      toast.error('Aanmaken mislukt');
    }
  };

  const handleAddCard = async (list_id, title) => {
    try {
      const res = await contentPlannerAPI.createCard({ list_id, title });
      setCards(prev => [...prev, res.data]);
    } catch {
      toast.error('Kaart aanmaken mislukt');
    }
  };

  const handleCardUpdate = (updated) => {
    setCards(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
    setOpenCard(prev => prev && prev.id === updated.id ? { ...prev, ...updated } : prev);
  };

  const handleCardDelete = (cardId) => {
    setCards(prev => prev.filter(c => c.id !== cardId));
  };

  const handleRenameList = async (list_id, title) => {
    try {
      await contentPlannerAPI.updateList(list_id, { title });
      setLists(prev => prev.map(l => l.id === list_id ? { ...l, title } : l));
    } catch {
      toast.error('Hernoemen mislukt');
    }
  };

  const handleChangeListColor = async (list_id, color) => {
    try {
      await contentPlannerAPI.updateList(list_id, { color });
      setLists(prev => prev.map(l => l.id === list_id ? { ...l, color } : l));
    } catch {
      toast.error('Kleur wijzigen mislukt');
    }
  };

  const handleDeleteList = async (list_id) => {
    try {
      await contentPlannerAPI.deleteList(list_id);
      setLists(prev => prev.filter(l => l.id !== list_id));
      setCards(prev => prev.filter(c => c.list_id !== list_id));
      toast.success('Lijst verwijderd');
    } catch {
      toast.error('Verwijderen mislukt');
    }
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId, type } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    if (type === 'LIST') {
      const newLists = Array.from(lists);
      const [moved] = newLists.splice(source.index, 1);
      newLists.splice(destination.index, 0, moved);
      setLists(newLists.map((l, idx) => ({ ...l, position: idx })));
      try {
        await contentPlannerAPI.reorderLists(newLists.map(l => l.id));
      } catch {
        toast.error('Lijst herordenen mislukt');
        fetchBoard();
      }
      return;
    }

    // CARD drag
    const cardId = draggableId;
    const newListId = destination.droppableId;
    const oldListId = source.droppableId;

    // Optimistic update
    setCards(prev => {
      const moving = prev.find(c => c.id === cardId);
      if (!moving) return prev;
      const others = prev.filter(c => c.id !== cardId);
      // Re-bouw lijsten
      const newListCards = others.filter(c => c.list_id === newListId).sort((a, b) => (a.position || 0) - (b.position || 0));
      newListCards.splice(destination.index, 0, { ...moving, list_id: newListId });
      const oldListCards = oldListId !== newListId
        ? others.filter(c => c.list_id === oldListId).sort((a, b) => (a.position || 0) - (b.position || 0))
        : [];
      const restCards = others.filter(c => c.list_id !== newListId && c.list_id !== oldListId);
      const rebuilt = [
        ...restCards,
        ...newListCards.map((c, idx) => ({ ...c, position: idx })),
        ...oldListCards.map((c, idx) => ({ ...c, position: idx })),
      ];
      return rebuilt;
    });

    try {
      await contentPlannerAPI.moveCard(cardId, newListId, destination.index);
    } catch {
      toast.error('Verplaatsen mislukt');
      fetchBoard();
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-3.5rem)] md:h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-border bg-background/80 backdrop-blur sticky top-0 z-10">
        <div>
          <h1 className="text-lg md:text-xl font-semibold tracking-tight">Content Planner</h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            Plan en organiseer je content. Kaarten met een datum verschijnen automatisch in de agenda.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {users.length > 0 && (
            <select
              value={assigneeFilter || 'all'}
              onChange={(e) => setAssigneeFilter(e.target.value === 'all' ? '' : e.target.value)}
              className="flex h-8 rounded-md border border-input bg-background px-2 py-1 text-xs shadow-sm"
            >
              <option value="all">Alle leden</option>
              {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          )}
          <Badge variant="outline" className="hidden md:inline-flex gap-1 text-xs">
            <ListChecks className="w-3 h-3" />
            {cards.length} kaarten
          </Badge>
        </div>
      </div>

      {/* Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="board" type="LIST" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex gap-3 p-4 h-full items-start min-w-max"
              >
                {lists.map((list, idx) => (
                  <BoardList
                    key={list.id}
                    list={list}
                    cards={cardsByList[list.id] || []}
                    index={idx}
                    onAddCard={handleAddCard}
                    onCardClick={setOpenCard}
                    onRenameList={handleRenameList}
                    onDeleteList={handleDeleteList}
                    onChangeListColor={handleChangeListColor}
                  />
                ))}
                {provided.placeholder}

                {/* Add list */}
                <div className="flex-shrink-0 w-[290px]">
                  {addingList ? (
                    <div className="bg-muted/50 rounded-lg p-2.5 space-y-2">
                      <Input
                        value={newListTitle}
                        onChange={(e) => setNewListTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddList(); if (e.key === 'Escape') { setAddingList(false); setNewListTitle(''); } }}
                        placeholder="Voer een lijsttitel in..."
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleAddList}>Lijst toevoegen</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setAddingList(false); setNewListTitle(''); }}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setAddingList(true)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm bg-muted/30 hover:bg-muted/60 rounded-lg text-muted-foreground hover:text-foreground transition-colors border border-dashed border-border"
                    >
                      <Plus className="w-4 h-4" /> Lijst toevoegen
                    </button>
                  )}
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <CardModal
        open={!!openCard}
        card={openCard}
        lists={lists}
        users={users}
        onClose={() => setOpenCard(null)}
        onUpdate={handleCardUpdate}
        onDelete={handleCardDelete}
      />
    </div>
  );
}
